import axios from 'axios';

// Local dev: set REACT_APP_API_SERVER_URL (e.g. http://localhost:8002 or http://localhost:3002). Production: set at build time, or leave unset if the app is served same-origin as /api.
const explicitApiUrl = (process.env.REACT_APP_API_SERVER_URL || '').trim();
const baseURL = explicitApiUrl ? `${explicitApiUrl}/api` : '/api';

if (process.env.NODE_ENV === 'development' && !explicitApiUrl) {
  // eslint-disable-next-line no-console
  console.warn(
    '[axios] REACT_APP_API_SERVER_URL is empty: set it in client/.env (e.g. http://localhost:8002) so API calls reach your backend.'
  );
}

// Create axios instance with proper configuration
const axiosInstance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    // Adding additional headers that might help with CORS
    'Accept': 'application/json'
  },
  maxContentLength: 50 * 1024 * 1024, // 50MB
  maxBodyLength: 50 * 1024 * 1024, // 50MB
  // Set withCredentials based on environment
  withCredentials: false, // Changed to false by default
  // Increased timeout to handle slow backend responses
  timeout: 120000 // 120 seconds timeout (increased from 60)
});

// Add request interceptor to dynamically get the token before each request
axiosInstance.interceptors.request.use(
  config => {
    // When sending FormData (e.g. file upload), do NOT set Content-Type so the browser sets multipart/form-data with boundary
    if (config.data && typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // SDK endpoints are served behind Vercel routes that set
    // `Access-Control-Allow-Origin: *` at the edge (so the SDK can be embedded
    // on any customer domain). Browsers reject `*` when credentials are sent,
    // so we strip cookies for every `/sdk/` call. SDK auth is bearer-token.
    if (config.url && config.url.includes('/sdk/')) {
      config.withCredentials = false;
    } else {
      // For all other endpoints, including team routes, use credentials
      config.withCredentials = true;
    }
    
    // Get token dynamically on each request - not from closure
    // Try both accessToken and token, as different parts of the app might use different keys
    const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Special handling for team routes
    if (config.url && config.url.includes('/team/')) {
      // Ensure content type is always set correctly for all team routes
      config.headers['Content-Type'] = 'application/json';
      
      // Ensure Authorization header is set for team routes
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Boards + chat + workflows + evals + issues: send current team so the
    // server scopes to the team the user is viewing (esp. the shared demo team,
    // which isn't the user's default team).
    if (config.url && (
      config.url.includes('/boards') ||
      config.url.includes('/dashboards') ||
      config.url.includes('/chat') ||
      config.url.includes('/workflows') ||
      config.url.includes('/evals') ||
      config.url.includes('/issues') ||
      config.url.includes('/insights')
    )) {
      try {
        const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
        const teamId = selectedTeamData.id ?? selectedTeamData._id ?? null;
        if (teamId != null && teamId !== '') {
          config.headers['X-Team-Id'] = String(teamId);
        }
      } catch (_) {}
    }
    
    return config;
  },
  error => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

function isCanceledRequest(error) {
  if (!error) return false;
  if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError') {
    return true;
  }
  const msg = typeof error.message === 'string' ? error.message.toLowerCase() : '';
  return msg === 'canceled' || msg === 'cancelled';
}

// --- Session refresh: single-flight + involuntary-logout guard --------------
// On app load many providers (team, subscription, preload, …) fire API calls at
// once. If the access token is stale they ALL 401 together. Without coordination
// each one POSTs /auth/refresh-token independently — and the first success used
// to invalidate the token the others were mid-flight with, so a valid 30-day
// session got nuked and the user was bounced to /login. We collapse concurrent
// refreshes into ONE shared promise, and only tear the session down on a
// *definitive* auth rejection (401/403 from the refresh endpoint) — never on a
// transient network/5xx/timeout blip.
let refreshPromise = null;

function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${baseURL}/auth/refresh-token`, {}, { withCredentials: true })
      .then(res => {
        const newToken = res?.data?.accessToken;
        if (!newToken) {
          const err = new Error('Refresh response missing accessToken');
          err.definitiveAuthFailure = true;
          throw err;
        }
        localStorage.setItem('accessToken', newToken);
        return newToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function isDefinitiveAuthFailure(err) {
  if (err?.definitiveAuthFailure) return true;
  const status = err?.response?.status;
  return status === 401 || status === 403;
}

function clearSessionAndRedirect() {
  // In-memory caches (teamService) must drop everything: a different user
  // logging in on the same tab would otherwise be served the previous user's
  // team row and role for up to the cache TTL. Signalled by event rather than
  // import — teamService imports this module, so a direct call would be a
  // circular dependency.
  try { window.dispatchEvent(new Event('trodo:session-cleared')); } catch (_) {}
  localStorage.removeItem('accessToken');
  localStorage.removeItem('User');
  localStorage.removeItem('selectedTeam');
  localStorage.removeItem('selectedTeamData');
  localStorage.removeItem('selectedWebsite');
  localStorage.removeItem('subscriptionStatus');
  if (window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
    window.location.href = '/login';
  }
}

// Add response interceptor to handle common errors
axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // AbortController / duplicate in-flight request — not a real failure; do not log
    if (isCanceledRequest(error)) {
      return Promise.reject(error);
    }
    
    // Check if this is a network error (no response from server)
    const isNetworkError = !error.response && (
      error.message === 'Network Error' || 
      error.code === 'ERR_NETWORK' ||
      error.code === 'ECONNREFUSED' ||
      error.code === 'ETIMEDOUT' ||
      error.message?.includes('Failed to fetch')
    );
    
    // For network errors, suppress console errors and handle them more gracefully
    if (isNetworkError) {
      // Implement retry logic for network errors (but not timeouts)
      if (!originalRequest._retry && error.code !== 'ECONNABORTED') {
        originalRequest._retry = true;
        
        // Add a small delay before retrying
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Return a new request
        return axiosInstance(originalRequest);
      }
      
      // After retry fails, return a more user-friendly error
      // This prevents the error from bubbling up to React's error overlay
      const networkError = new Error('Unable to connect to server. Please check your connection or try again later.');
      networkError.isNetworkError = true;
      networkError.suppressErrorOverlay = true; // Flag to suppress React error overlay
      networkError.originalError = error;
      return Promise.reject(networkError);
    }
    
    // For non-network errors, log normally
    console.error('API Response Error:', {
      url: originalRequest?.url,
      status: error.response?.status,
      message: error.message,
      data: error.response?.data 
    });
    
    // Handle timeout errors specifically
    if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
      // Don't retry timeout errors immediately - let the calling code handle it
      return Promise.reject(error);
    }
    
    // Handle 401 (access token expired) — try a single-flight refresh, then
    // retry the original request. Concurrent 401s all await the SAME refresh.
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Only end the session if the refresh was *definitively* rejected
        // (expired/invalid refresh token). Transient failures — network, 5xx,
        // timeout — must NOT log the user out; keep the session and let the app
        // retry. This is what stops the "logged out for no reason" bounces.
        if (isDefinitiveAuthFailure(refreshError)) {
          clearSessionAndRedirect();
        }
        return Promise.reject(error);
      }
    }

    // Log the error for debugging
    console.error("API Error:", error.message);

    // A 401 that survives a refresh + retry means the token is genuinely no
    // longer accepted — end the session.
    if (error.response && error.response.status === 401) {
      clearSessionAndRedirect();
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;